import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import SocialIcons from "../components/app/SocialIcons";
import InstallQRCard from "../components/app/InstallQRCard";
import AvatarPicker from "../components/app/AvatarPicker";
import HelpfulCamperBadge from "../components/app/HelpfulCamperBadge";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Instagram, Facebook, Loader2, User, Sparkles, Check } from "lucide-react";
import { SOCIAL } from "../lib/social";
import SavedFestivalsSection from "../components/app/SavedFestivalsSection";

/**
 * Account / profile page.
 *
 * Two purposes:
 *   1. Show the user their own account (name, email, Pro status).
 *   2. Let them link their personal Instagram + Facebook handles, so any
 *      future review/trip share can pre-fill captions with their tag too.
 *      Linked handles also unlock our "we'll @-mention you when we repost"
 *      promise, which makes tagging us feel more like a fair trade.
 */
export default function Profile() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [ig, setIg] = useState("");
  const [fb, setFb] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    setIg(user.instagram_handle || "");
    setFb(user.facebook_handle || "");
  }, [user, authLoading, nav]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        instagram_handle: ig.trim().replace(/^@/, ""),
        facebook_handle: fb.trim().replace(/^@/, ""),
      });
      if (refreshUser) await refreshUser();
      setSavedAt(Date.now());
      toast.success(t("profile.savedToast"));
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const savePicture = async (nextDataUrl) => {
    try {
      await api.patch("/auth/me", { picture: nextDataUrl ?? "" });
      if (refreshUser) await refreshUser();
      toast.success(nextDataUrl ? "Profile picture updated" : "Profile picture removed");
    } catch (err) { toast.error(formatErr(err)); }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#fafafa]" data-testid="profile-loading">
        <Header />
        <div className="flex items-center justify-center py-20 text-[#888]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="profile-page">
      <Header active="profile" />

      <main className="max-w-[820px] mx-auto px-6 lg:px-10 py-12">
        <div className="eyebrow mb-2">{t("profile.eyebrow")}</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-8">
          {t("profile.hello", { name: user.name?.split(" ")[0] || t("profile.fallbackName") })} 🏕️
        </h1>

        {/* Account summary */}
        <section className="border-2 hairline-strong bg-white p-6 mb-8" data-testid="profile-account">
          <header className="flex items-center gap-3 mb-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#ff4500]" data-testid="profile-avatar" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#fff4ee] inline-flex items-center justify-center">
                <User className="w-4 h-4 text-[#ff4500]" strokeWidth={2.5} />
              </div>
            )}
            <div>
              <div className="font-display text-lg">{user.name}</div>
              <div className="mono text-xs text-[#666] break-all">{user.email}</div>
            </div>
            {user.is_pro && (
              <span className="ml-auto text-[10px] mono uppercase tracking-wider px-2.5 py-1 bg-[#111] text-white">
                Pro
              </span>
            )}
          </header>
          <div className="mb-4">
            <AvatarPicker value={user.picture || null} onChange={savePicture} size={88} label="Profile picture" />
          </div>
          <div className="mb-4" data-testid="profile-helpful-badge-wrap">
            <HelpfulCamperBadge self />
          </div>
          <p className="text-sm text-[#555] leading-relaxed">
            {t("profile.passwordHint_pre")} <a href="/forgot-password" className="link-underline">{t("profile.passwordHint_link")}</a> {t("profile.passwordHint_post")}
          </p>
        </section>

        {/* Linked socials */}
        <section className="border-2 hairline-strong bg-white p-6 mb-8" data-testid="profile-socials">
          <header className="mb-5">
            <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> {t("profile.socialsEyebrow")}
            </div>
            <h2 className="font-display text-xl mt-1">{t("profile.socialsTitle")}</h2>
            <p className="text-sm text-[#555] leading-relaxed mt-1.5 max-w-xl">
              {t("profile.socialsBody")}
            </p>
          </header>

          <form onSubmit={save} className="space-y-4">
            <label className="block">
              <span className="eyebrow block mb-1.5">{t("profile.yourInstagram")}</span>
              <div className="flex items-center border hairline focus-within:border-[#ff4500]">
                <span className="bg-[#fafafa] border-r hairline px-3 py-2.5 inline-flex items-center gap-1 text-[#888]">
                  <Instagram className="w-4 h-4" strokeWidth={1.7} />
                  <span className="mono text-sm">@</span>
                </span>
                <input
                  type="text"
                  value={ig}
                  onChange={(e) => setIg(e.target.value)}
                  placeholder={t("profile.instagramPlaceholder")}
                  maxLength={60}
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                  data-testid="profile-instagram-input"
                />
              </div>
            </label>

            <label className="block">
              <span className="eyebrow block mb-1.5">{t("profile.yourFacebook")}</span>
              <div className="flex items-center border hairline focus-within:border-[#ff4500]">
                <span className="bg-[#fafafa] border-r hairline px-3 py-2.5 inline-flex items-center gap-1 text-[#888]">
                  <Facebook className="w-4 h-4" strokeWidth={1.7} />
                  <span className="mono text-sm">facebook.com/</span>
                </span>
                <input
                  type="text"
                  value={fb}
                  onChange={(e) => setFb(e.target.value)}
                  placeholder={t("profile.facebookPlaceholder")}
                  maxLength={60}
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                  data-testid="profile-facebook-input"
                />
              </div>
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="profile-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
                {t("profile.save")}
              </button>
              {savedAt && (
                <span className="text-xs mono text-[#22c55e]">{t("profile.savedInline")}</span>
              )}
            </div>
          </form>
        </section>

        {/* Install via QR — handy for desktop visitors */}
        <InstallQRCard />

        {/* iter122 — Saved festivals + reminder status */}
        <SavedFestivalsSection />

        {/* Follow CampCompass UK */}
        <section className="border-2 hairline-strong bg-white p-6" data-testid="profile-follow-cc">
          <header className="mb-4">
            <div className="eyebrow text-[#ff4500]">{t("profile.followCcEyebrow")}</div>
            <h2 className="font-display text-xl mt-1">{t("profile.followCcTitle")}</h2>
            <p className="text-sm text-[#555] leading-relaxed mt-1.5">
              {t("profile.followCcBody")}
            </p>
          </header>
          <SocialIcons size={22} showLabels testIdPrefix="profile-social-cc" className="!gap-6" />
        </section>
      </main>

      <Footer />
    </div>
  );
}
